<template>
  <div class="cookie-comply__modal">
    <div class="cookie-comply__modal-middle">
      <div class="cookie-comply__modal-inner">

        <header class="cookie-comply__modal-header">
          <slot name="modal-header">
            <h3 class="pb-1">{{ modalHeader[language] }}</h3>
          </slot>
        </header>

        <main class="cookie-comply__modal-content">
          <div v-for="(preference, index) in preferences" :key="index">
            <slot name="modal-body" :preference="preference" :index="index">
              <div class="cookie-comply__modal-switches py-1 mt-1">
                <h2>{{ preference.title[language] }}</h2>
                <cookie-comply-switch
                  :value="preference.value[language]"
                  :is-required="preference.isRequired"
                  :is-default-enable="preference.isEnable"
                  @update:checkbox="handleCheckboxUpdate"
                />
              </div>
              <p class="py-1" style="font-size:16px;">{{ preference.description[language] }}</p>
              <div class="table-responsive">
                <table class="table" style="font-size: 13px;">
                  <thead>
                    <tr>
                      <th scope="col">
                        {{ tableHeaders[0][language] }}
                      </th>
                      <th scope="col">
                        {{ tableHeaders[1][language] }}
                      </th>
                      <th scope="col">
                        {{ tableHeaders[2][language] }}
                      </th>
                      <th scope="col">
                        {{ tableHeaders[3][language] }}
                      </th>
                      <th scope="col">
                        {{ tableHeaders[4][language] }}
                      </th>
                      <th scope="col">
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in preference.items[language]"
                      :key="item.value"
                    >
                      <th scope="row">{{ item.label }}</th>
                      <td>{{ item.value }}</td>
                      <td>{{ item.expiration }}</td>
                      <td>{{ item.domain }}</td>
                      <td>{{ item.company }}</td>
                      <td><a :href="item.privacy">{{ policyPrivacyLabel[language] }}</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </slot>
          </div>
        </main>

        <footer class="cookie-comply__modal-footer">
          <slot name="modal-footer">
            <cookie-comply-button @handleClick="onSaveConfiguration">
              {{ saveButton[language] }}
            </cookie-comply-button>
          </slot>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref } from 'vue'
import CookieComplyButton from './CookieComplyButton.vue'
import CookieComplySwitch from './CookieComplySwitch.vue'

// Definicja typów dla propsów
const props = defineProps({
  preferences: {
    type: Array as () => Array<any>, // Typuj preferences zgodnie z ich strukturą
    default: () => []
  },
  language: {
    type: String,
    required: true
  }
})

// Emitowanie zdarzeń
const emit = defineEmits<{
  (e: 'cookie-comply-save', data: any): void
  (e: 'cookie-comply-close'): void
}>()

// Zmienne lokalne
const checkedValues = ref<string[]>([])
const modalHeader = {
  pl: 'Zaawansowane ustawienia prywatności',
  en: 'Advanced privacy settings',
}
const tableHeaders = [
  { pl: 'Nazwa pliku cookie', en: 'Name of cookie file' },
  { pl: 'Przykładowa wartość', en: 'Example value' },
  { pl: 'Okres ważności', en: 'Validity period' },
  { pl: 'Domena', en: 'Domain' },
  { pl: 'Dostawcy', en: 'Providers' },
]
const saveButton = {
  pl: 'Zapisz',
  en: 'Save',
}
const policyPrivacyLabel = {
  pl: 'Polityka Prywatności',
  en: 'Privacy Policy',
}

// Metody
const handleCheckboxUpdate = ({ value, isEnable }: { value: string, isEnable: boolean }) => {
  if (isEnable) {
    checkedValues.value.push(value)
  } else {
    checkedValues.value.splice(checkedValues.value.indexOf(value), 1)
  }
}

const onSaveConfiguration = () => {
  emit('cookie-comply-save', checkedValues.value)
}

const onCloseModal = () => {
  emit('cookie-comply-close')
}
</script>

<style scoped>
.cookie-comply__modal {
  display: table;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: var(--background-overlay);
}

.cookie-comply__modal-middle {
  display: table-cell;
  vertical-align: middle;
}

.cookie-comply__modal-inner {
  position: relative;
  margin-left: auto;
  margin-right: auto;
  width: 400px;
  padding: var(--spacing-lg);
  background-color: var(--color-white);
  border-radius: var(--spacing-sm);
  box-shadow: var(--box-shadow);
  max-height: 90svh;
  overflow: auto;
}

.cookie-comply__back-arrow {
  position: absolute;
  left: var(--spacing-lg);
  cursor: pointer;
}

.cookie-comply__modal-content {
  overflow-y: scroll;
  max-height: 400px;
  text-align: left;
}

.cookie-comply__modal-switches {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: var(--border-color-lightest);
}

@media (max-width: 480px) {
  .cookie-comply__modal-middle {
    padding: var(--spacing-md);
  }

  .cookie-comply__modal-inner {
    width: auto;
  }
}

.cookie-comply__modal-header {
  border-bottom: var(--border-color-light);
}

.cookie-comply__modal-footer {
  border-top: var(--border-color-light);
  padding-top: var(--spacing-lg);
}
</style>
